import { useCallback, useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import type { AnalyticsPayload, DayPayload, Direction, TimeEntry } from "../shared/types";
import { addDays, formatMinutes } from "../shared/time";
import { api, ApiError } from "./api";
import { DirectionBadge, ErrorBanner, LoadingBlock } from "./components";

export type Granularity = "day" | "week" | "month";
type TrendSegmentSelection = { date: string; directionId: string };
const utcDate = (value: string) => new Date(`${value}T00:00:00Z`);
export function rangeFor(anchor: string, granularity: Granularity) {
  if (granularity === "day") return { from: anchor, to: anchor };
  return { from: addDays(anchor, granularity === "week" ? -6 : -29), to: anchor };
}
export function stepAnchor(anchor: string, granularity: Granularity, direction: number, max?: string) {
  const step = granularity === "day" ? 1 : granularity === "week" ? 7 : 30;
  const result = addDays(anchor, direction * step);
  return max && result > max ? max : result;
}
export function rangeTitle(from: string, to: string, granularity: Granularity) {
  if (granularity === "day") return new Intl.DateTimeFormat("zh-CN", { month: "long", day: "numeric", weekday: "long", timeZone: "UTC" }).format(utcDate(from));
  return `${from.replaceAll("-", "/")} – ${to.replaceAll("-", "/")}`;
}

export function newestFirst<T extends { date: string }>(items: readonly T[]): T[] {
  return [...items].sort((left, right) => right.date.localeCompare(left.date));
}

const UNTRACKED_COLOR = "#e5e8ee";
const MINUTES_PER_DAY = 24 * 60;

export interface DailyTimelineSegment {
  key: string;
  startMinute: number;
  endMinute: number;
  activityId: string | null;
  activityName: string;
  directionId: string | null;
  note: string;
  untracked: boolean;
}

function appendTimelineSegment(segments: DailyTimelineSegment[], segment: DailyTimelineSegment) {
  if (segment.endMinute <= segment.startMinute) return;
  const previous = segments.at(-1);
  const sameTrackedContent = previous && !previous.untracked && !segment.untracked
    && previous.activityId === segment.activityId && previous.note === segment.note;
  if (previous && previous.endMinute === segment.startMinute && ((previous.untracked && segment.untracked) || sameTrackedContent)) {
    previous.endMinute = segment.endMinute;
    return;
  }
  segments.push(segment);
}

export function dailyTimelineSegments(entries: readonly TimeEntry[]): DailyTimelineSegment[] {
  const byHour = new Map<number, TimeEntry[]>();
  for (const entry of entries) {
    const hourEntries = byHour.get(entry.slotHour) ?? [];
    hourEntries.push(entry);
    byHour.set(entry.slotHour, hourEntries);
  }

  const segments: DailyTimelineSegment[] = [];
  for (let hour = 0; hour < 24; hour += 1) {
    const hourStart = hour * 60;
    const hourEnd = hourStart + 60;
    let cursor = hourStart;
    const hourEntries = [...(byHour.get(hour) ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
    for (const entry of hourEntries) {
      const duration = Math.max(0, Math.min(entry.durationMinutes, hourEnd - cursor));
      const note = entry.note.trim();
      appendTimelineSegment(segments, {
        key: entry.id,
        startMinute: cursor,
        endMinute: cursor + duration,
        activityId: entry.activityId,
        activityName: entry.activityName,
        directionId: entry.directionId,
        note,
        untracked: false,
      });
      cursor += duration;
      if (cursor >= hourEnd) break;
    }
    appendTimelineSegment(segments, {
      key: `untracked-${hour}`,
      startMinute: cursor,
      endMinute: hourEnd,
      activityId: null,
      activityName: "未记录",
      directionId: null,
      note: "",
      untracked: true,
    });
  }
  return segments;
}

export function timelineClock(minute: number) {
  if (minute >= MINUTES_PER_DAY) return "24:00";
  return `${String(Math.floor(minute / 60)).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`;
}

export function dailyTrendHeight(minutes: number) {
  return `${Math.max(0, Math.min(minutes, MINUTES_PER_DAY)) / MINUTES_PER_DAY * 100}%`;
}

function directionGradient(directions: Direction[], totals: Record<string, number>, denominator: number, unrecorded: number) {
  if (!denominator) return "#eef0f4";
  let cursor = 0;
  const values = [
    ...directions.map((direction) => ({ color: direction.color, minutes: totals[direction.id] ?? 0 })),
    { color: UNTRACKED_COLOR, minutes: unrecorded },
  ].filter((item) => item.minutes > 0);
  const segments = values.map((item, index) => {
    const start = cursor;
    cursor = index === values.length - 1 ? 100 : cursor + item.minutes / denominator * 100;
    return `${item.color} ${start}% ${cursor}%`;
  });
  return `conic-gradient(${segments.join(", ")})`;
}

export function AnalyticsPage({ today }: { today: string }) {
  const [granularity, setGranularity] = useState<Granularity>("day");
  const [anchor, setAnchor] = useState(today);
  const [analytics, setAnalytics] = useState<AnalyticsPayload | null>(null);
  const [day, setDay] = useState<DayPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hoveredTrendSegment, setHoveredTrendSegment] = useState<TrendSegmentSelection | null>(null);
  const [focusedTrendSegment, setFocusedTrendSegment] = useState<TrendSegmentSelection | null>(null);
  const [pinnedTrendSegment, setPinnedTrendSegment] = useState<TrendSegmentSelection | null>(null);
  const [hoveredTimelineSegment, setHoveredTimelineSegment] = useState<string | null>(null);
  const [focusedTimelineSegment, setFocusedTimelineSegment] = useState<string | null>(null);
  const [pinnedTimelineSegment, setPinnedTimelineSegment] = useState<string | null>(null);
  const range = useMemo(() => rangeFor(anchor, granularity), [anchor, granularity]);
  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [result, dayResult] = await Promise.all([
        api<AnalyticsPayload>(`/analytics?from=${range.from}&to=${range.to}&granularity=${granularity}`),
        granularity === "day" ? api<DayPayload>(`/days/${range.from}`) : Promise.resolve(null),
      ]);
      setAnalytics(result); setDay(dayResult);
    } catch (cause) { setError(cause instanceof ApiError ? cause.message : "统计读取失败。"); }
    finally { setLoading(false); }
  }, [range.from, range.to, granularity]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    setHoveredTrendSegment(null); setFocusedTrendSegment(null); setPinnedTrendSegment(null);
    setHoveredTimelineSegment(null); setFocusedTimelineSegment(null); setPinnedTimelineSegment(null);
  }, [range.from, range.to, granularity]);
  const donut = analytics ? directionGradient(analytics.directions, analytics.totals, analytics.denominatorMinutes, analytics.unrecordedMinutes) : "#eef0f4";
  const activeTrendSegment = pinnedTrendSegment ?? hoveredTrendSegment ?? focusedTrendSegment;
  const activeTrendDay = activeTrendSegment && analytics ? analytics.days.find((item) => item.date === activeTrendSegment.date) : undefined;
  const activeTrendDirection = activeTrendSegment && analytics ? analytics.directions.find((item) => item.id === activeTrendSegment.directionId) : undefined;
  const activeTrendDetails = activeTrendSegment && activeTrendDay ? activeTrendDay.entryDetails.filter((item) => item.directionId === activeTrendSegment.directionId) : [];
  const timelineSegments = useMemo(() => dailyTimelineSegments(day?.entries ?? []), [day]);
  const activeTimelineKey = pinnedTimelineSegment ?? hoveredTimelineSegment ?? focusedTimelineSegment;
  const activeTimelineSegment = activeTimelineKey ? timelineSegments.find((segment) => segment.key === activeTimelineKey) : undefined;
  const activeTimelineDirection = activeTimelineSegment && analytics
    ? analytics.directions.find((direction) => direction.id === activeTimelineSegment.directionId)
    : undefined;
  return <div className="page analytics-page">
    <header className="page-header analytics-header"><div><p className="eyebrow">TIME ANALYTICS</p><h1>时间统计</h1><p>从每天的分钟，看见长期的方向。</p></div><div className="analytics-controls"><div className="analytics-nav"><strong>{rangeTitle(range.from, range.to, granularity)}</strong><button className="date-step" onClick={() => setAnchor(stepAnchor(anchor, granularity, -1))}>←</button><input aria-label="选择统计日期" type="date" value={anchor} max={today} onChange={(event) => setAnchor(event.target.value)} /><button className="date-step" disabled={range.to >= today} onClick={() => setAnchor(stepAnchor(anchor, granularity, 1, today))}>→</button><button className="text-button" onClick={() => setAnchor(today)}>今天</button></div><div className="period-tabs">{(["day", "week", "month"] as Granularity[]).map((item) => <button key={item} className={granularity === item ? "active" : ""} onClick={() => { setGranularity(item); setAnchor(today); }}>{item === "day" ? "日" : item === "week" ? "近7天" : "近30天"}</button>)}</div></div></header>
    {error && <ErrorBanner message={error} onDismiss={() => setError("")} />}
    {loading || !analytics ? <LoadingBlock /> : <>
      <section className="analytics-overview card"><div className="analytics-overview-metric"><span>已记录</span><strong>{formatMinutes(analytics.recordedMinutes)}</strong><small>{analytics.days.length} 个有效日期</small></div><i /><div className="analytics-overview-metric"><span>记录完成率</span><strong>{Math.round(analytics.completionRate * 100)}%</strong><small>空白 {formatMinutes(analytics.unrecordedMinutes)}</small></div></section>
      <section className="analytics-grid">
        <div className="card distribution-card"><div className="section-heading"><div><h2>方向分布</h2><p>占可记录总时间的比例</p></div></div><div className="donut-wrap"><div className="donut" style={{ background: donut }}><div><strong>{Math.round(analytics.completionRate * 100)}%</strong><span>记录完成</span></div></div><div className="donut-legend">{analytics.directions.map((direction) => <div key={direction.id}><DirectionBadge direction={direction} /><span>{analytics.denominatorMinutes ? Math.round((analytics.totals[direction.id] ?? 0) / analytics.denominatorMinutes * 100) : 0}%</span><strong>{formatMinutes(analytics.totals[direction.id] ?? 0)}</strong></div>)}<div><span className="category-badge"><span className="category-dot untracked-dot" />未记录</span><span>{analytics.denominatorMinutes ? Math.round(analytics.unrecordedMinutes / analytics.denominatorMinutes * 100) : 0}%</span><strong>{formatMinutes(analytics.unrecordedMinutes)}</strong></div></div></div></div>
        <div className="card trend-card" onMouseLeave={() => { setHoveredTrendSegment(null); setHoveredTimelineSegment(null); }}><div className="section-heading"><div><h2>{granularity === "day" ? "日时间轴" : "每日趋势"}</h2><p>{granularity === "day" ? "同事项、同备注的连续记录会自动合并；悬停查看这段详情" : "灰色条纹代表未记录时间；点击方向色块可固定明细"}</p></div></div>
          {granularity === "day" && day ? <div className="daily-timeline-shell">
            <div className="daily-timeline" role="group" aria-label={`${range.from} 的 24 小时时间轴`}>
              {timelineSegments.map((segment) => {
                const duration = segment.endMinute - segment.startMinute;
                const direction = analytics.directions.find((item) => item.id === segment.directionId);
                const selected = activeTimelineKey === segment.key;
                const pinned = pinnedTimelineSegment === segment.key;
                const label = `${timelineClock(segment.startMinute)}–${timelineClock(segment.endMinute)}，${segment.activityName}，${formatMinutes(duration)}${segment.note ? `，备注：${segment.note}` : "，无备注"}`;
                return <button
                  type="button"
                  key={segment.key}
                  className={`daily-timeline-segment${segment.untracked ? " untracked" : ""}`}
                  style={{ flexGrow: duration, background: segment.untracked ? undefined : direction?.color }}
                  aria-label={`${label}，点击固定详情`}
                  aria-describedby={selected ? "daily-timeline-tooltip" : undefined}
                  aria-pressed={pinned}
                  onMouseEnter={() => setHoveredTimelineSegment(segment.key)}
                  onFocus={() => setFocusedTimelineSegment(segment.key)}
                  onBlur={() => setFocusedTimelineSegment((current) => current === segment.key ? null : current)}
                  onClick={() => { setPinnedTimelineSegment(pinned ? null : segment.key); if (pinned) setHoveredTimelineSegment(null); }}
                />;
              })}
            </div>
            <div className="daily-timeline-axis" aria-hidden="true">{[0, 3, 6, 9, 12, 15, 18, 21, 24].map((hour) => <span key={hour}>{String(hour).padStart(2, "0")}:00</span>)}</div>
            {activeTimelineSegment && <div
              className={`daily-timeline-tooltip${pinnedTimelineSegment ? " pinned" : ""}`}
              id="daily-timeline-tooltip"
              role={pinnedTimelineSegment ? "dialog" : "tooltip"}
              aria-label={pinnedTimelineSegment ? "已固定的时间段详情" : undefined}
              style={{ "--timeline-center": `${(activeTimelineSegment.startMinute + activeTimelineSegment.endMinute) / 2 / MINUTES_PER_DAY * 100}%` } as CSSProperties}
            >
              <header><strong>{timelineClock(activeTimelineSegment.startMinute)}–{timelineClock(activeTimelineSegment.endMinute)}</strong><span>{formatMinutes(activeTimelineSegment.endMinute - activeTimelineSegment.startMinute)}</span>{pinnedTimelineSegment && <button type="button" className="trend-detail-close" aria-label="关闭固定详情" onClick={() => { setPinnedTimelineSegment(null); setHoveredTimelineSegment(null); setFocusedTimelineSegment(null); }}>×</button>}</header>
              <div className="daily-timeline-tooltip-body"><div><span>事项</span><strong>{activeTimelineSegment.activityName}</strong></div>{activeTimelineDirection && <div><span>方向</span><strong><i style={{ background: activeTimelineDirection.color }} />{activeTimelineDirection.name}</strong></div>}<div><span>备注</span><strong>{activeTimelineSegment.note || "无备注"}</strong></div></div>
            </div>}
          </div> : <div className="trend-chart-wrap">
            <div className={`bar-chart ${granularity}`}>{analytics.days.map((item) => {
              const unrecorded = Math.max(0, MINUTES_PER_DAY - item.recorded);
              const segmentTitle = (label: string, minutes: number) => `${label}：${formatMinutes(minutes)}（${Math.round(minutes / MINUTES_PER_DAY * 100)}%）`;
              const description = `${item.date}：${analytics.directions.map((direction) => segmentTitle(direction.name, item.directionTotals[direction.id] ?? 0)).join("；")}；${segmentTitle("未记录", unrecorded)}`;
              return <div className="bar-column" key={item.date} aria-label={description}><div className="bar-stack">{analytics.directions.map((direction) => {
                const minutes = item.directionTotals[direction.id] ?? 0;
                const selected = activeTrendSegment?.date === item.date && activeTrendSegment.directionId === direction.id;
                const pinned = pinnedTrendSegment?.date === item.date && pinnedTrendSegment.directionId === direction.id;
                const selection = { date: item.date, directionId: direction.id };
                return minutes > 0 && <button type="button" key={direction.id} className="trend-segment" aria-label={`${item.date}，${segmentTitle(direction.name, minutes)}，点击固定明细`} aria-describedby={selected ? "trend-detail-tooltip" : undefined} aria-pressed={pinned} title="点击固定明细" onMouseEnter={() => setHoveredTrendSegment(selection)} onFocus={() => setFocusedTrendSegment(selection)} onBlur={() => setFocusedTrendSegment((current) => current?.date === item.date && current.directionId === direction.id ? null : current)} onClick={() => { setPinnedTrendSegment(pinned ? null : selection); if (pinned) setHoveredTrendSegment(null); }} style={{ height: dailyTrendHeight(minutes), background: direction.color }} />;
              })}<i className="untracked" title={segmentTitle("未记录", unrecorded)} style={{ height: dailyTrendHeight(unrecorded) }} /></div><span>{granularity === "week" ? ["一", "二", "三", "四", "五", "六", "日"][new Date(`${item.date}T00:00:00Z`).getUTCDay() === 0 ? 6 : new Date(`${item.date}T00:00:00Z`).getUTCDay() - 1] : Number(item.date.slice(8)) % 5 === 1 ? String(Number(item.date.slice(8))) : ""}</span></div>;
            })}</div>
            {activeTrendDay && activeTrendDirection && <div className={`trend-detail-tooltip ${pinnedTrendSegment ? "pinned" : ""}`} id="trend-detail-tooltip" role={pinnedTrendSegment ? "dialog" : "tooltip"} aria-label={pinnedTrendSegment ? "已固定的时间明细" : undefined}>
              <header><strong>{activeTrendDay.date} · {activeTrendDirection.name}</strong><span>{formatMinutes(activeTrendDay.directionTotals[activeTrendDirection.id] ?? 0)} · {Math.round((activeTrendDay.directionTotals[activeTrendDirection.id] ?? 0) / MINUTES_PER_DAY * 100)}%</span>{pinnedTrendSegment && <button type="button" className="trend-detail-close" aria-label="关闭固定明细" onClick={() => { setPinnedTrendSegment(null); setHoveredTrendSegment(null); setFocusedTrendSegment(null); }}>×</button>}</header>
              <div className="distribution-detail-list">{activeTrendDetails.map((detail) => <div className="distribution-detail-row" key={detail.key}><div><strong>{detail.activityName}</strong><span>{formatMinutes(detail.minutes)}</span></div><p title={detail.note || "无备注"}>{detail.note || "无备注"}</p></div>)}</div>
            </div>}
          </div>}
        </div>
      </section>
      <section className="card records-table"><div className="section-heading"><div><h2>逐日明细</h2><p>方向时长与当天覆盖率</p></div></div><div className="table-scroll"><table><thead><tr><th>日期</th>{analytics.directions.map((direction) => <th key={direction.id}>{direction.name}</th>)}<th>未记录</th><th>完成率</th></tr></thead><tbody>{newestFirst(analytics.days).map((item) => <tr key={item.date}><td>{item.date}</td>{analytics.directions.map((direction) => <td key={direction.id}>{formatMinutes(item.directionTotals[direction.id] ?? 0)}</td>)}<td>{formatMinutes(item.unrecorded)}</td><td><div className="coverage-cell"><i><span style={{ width: `${item.coverage * 100}%` }} /></i>{Math.round(item.coverage * 100)}%</div></td></tr>)}</tbody></table></div></section>
    </>}
  </div>;
}
